import { useCallback, useState } from "react";

import { RiskyEmailPolicy } from "@signalist/shared-types";

import { List } from "../../../schema/list";
import {
	changedEmailOverrides,
	EmailOverrides,
	storedEmailOverridesOf,
} from "../utils/listEmailDeliverability.utils";

/** What the Email deliverability section reads and writes. */
export interface ListEmailOverridesState extends EmailOverrides {
	setEmailVerificationEnabled: (value: boolean | null) => void;
	setRiskyEmailPolicy: (value: RiskyEmailPolicy | null) => void;
}

interface PendingEdits {
	listId: number | undefined;
	values: Partial<EmailOverrides>;
}

/**
 * The per-list email-deliverability overrides on List Settings (PREC-4261).
 *
 * The stored values are read from the list on every render; the user's edits are kept beside them,
 * tagged with the list they were made on. So a refetch after a save shows the saved values, and
 * switching to another list (the page is not remounted) drops the edits instead of carrying them
 * over or bringing them back on return. `emailDeliverabilityUpdate` holds only the changed fields.
 */
export const useListEmailDeliverabilityOverrides = (listData: List | undefined) => {
	const listId = listData?.id;
	const [edits, setEdits] = useState<PendingEdits>({ listId, values: {} });

	const ownEdits = edits.listId === listId ? edits.values : {};
	const stored = storedEmailOverridesOf(listData);
	const current: EmailOverrides = { ...stored, ...ownEdits };
	const emailDeliverabilityUpdate = changedEmailOverrides(stored, current);
	const hasEmailDeliverabilityChanged = Object.keys(emailDeliverabilityUpdate).length > 0;

	// Edits made on another list are dropped (the page is not remounted on a switch), and so are
	// edits the stored values have caught up with (the refetch after a save): a later change on the
	// server is then shown instead of being masked by an old edit.
	if (edits.listId !== listId || (!hasEmailDeliverabilityChanged && Object.keys(ownEdits).length)) {
		setEdits({ listId, values: {} });
	}

	const edit = useCallback(
		(patch: Partial<EmailOverrides>) =>
			setEdits((previous) => ({
				listId,
				values: { ...(previous.listId === listId ? previous.values : {}), ...patch },
			})),
		[listId]
	);

	const overrides: ListEmailOverridesState = {
		...current,
		setEmailVerificationEnabled: (value) => edit({ emailVerificationEnabled: value }),
		setRiskyEmailPolicy: (value) => edit({ riskyEmailPolicy: value }),
	};

	return {
		overrides,
		hasEmailDeliverabilityChanged,
		emailDeliverabilityUpdate,
	};
};
